import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';
import { useRef } from 'react';

const multipliers = [
  {
    name: 'Category Reset',
    description: 'Redefine your market category to remove positioning constraints.',
    icon: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  },
  {
    name: 'Naming',
    description: 'Strategic naming that removes clarity bottlenecks and creates demand.',
    icon: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z',
  },
  {
    name: 'Positioning',
    description: 'Define exactly who you serve and remove market confusion.',
    icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7',
  },
  {
    name: 'Pricing',
    description: 'Design pricing that removes value capture bottlenecks.',
    icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  {
    name: 'Distribution',
    description: 'Build systematic channels that remove reach constraints.',
    icon: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z',
  },
  {
    name: 'Activation',
    description: 'Remove friction from first value to drive rapid adoption.',
    icon: 'M13 10V3L4 14h7v7l9-11h-7z',
  },
  {
    name: 'Narrative',
    description: 'Craft stories that remove awareness bottlenecks and create momentum.',
    icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  },
  {
    name: 'Partnerships',
    description: 'Strategic alliances that remove credibility and reach constraints.',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  },
  {
    name: 'Platform Model',
    description: 'Evolve from product to platform to remove growth ceiling.',
    icon: 'M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1v-2zM14 15a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1v-2z',
  },
  {
    name: 'Decision Speed',
    description: 'Increase execution velocity by removing structural confusion.',
    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  },
];

const MultipliersGrid = () => {
  const { ref, isInView } = useInView({ threshold: 0.1 });
  const sectionRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  // DESKTOP ONLY - Parallax background
  const bgY = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);

  return (
    <section ref={sectionRef} className="relative bg-dark-bg py-20 lg:py-40 overflow-hidden">
      {/* DESKTOP ONLY - Parallax background */}
      <motion.div style={{ y: bgY }} className="hidden lg:block absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-[500px] md:w-[700px] h-[300px] md:h-[400px] rounded-full bg-accent-blue/4 blur-[120px] md:blur-[160px]" />
      </motion.div>

      {/* MOBILE - Static background */}
      <div className="lg:hidden absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[300px] rounded-full bg-accent-blue/4 blur-[120px] opacity-60" />
      </div>

      <div ref={ref} className="max-w-[1200px] mx-auto px-6 md:px-8 relative z-10">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-[34px] leading-[1.2] md:text-5xl lg:text-6xl font-bold text-white text-center mb-12 md:mb-16 lg:mb-20"
        >
          Force Multipliers
        </motion.h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 lg:gap-10">
          {multipliers.map((multiplier, index) => (
            <motion.div
              key={multiplier.name}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.1 + index * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              className="bg-dark-card border border-dark-border rounded-xl md:rounded-2xl p-7 md:p-10 lg:p-12 hover:border-accent-blue transition-all duration-300 group flex flex-col items-center text-center"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 flex items-center justify-center mb-5 md:mb-8 lg:mb-10 mx-auto">
                <svg
                  className="w-full h-full text-accent-blue group-hover:scale-110 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d={multiplier.icon} />
                </svg>
              </div>
              <h3 className="text-[18px] leading-[1.4] md:text-2xl lg:text-3xl font-semibold text-white mb-3 md:mb-5 lg:mb-6">
                {multiplier.name}
              </h3>
              <p className="text-[14px] leading-[1.7] md:text-lg lg:text-xl text-gray-400">
                {multiplier.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MultipliersGrid;